import React, { Fragment, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import styles from "./Forms.module.css";
import TextInput from "../UILibrary/TextInput";
import Submission from "../UILibrary/Submission";
import FileUpload from "../UILibrary/Upload";

import { defaultValues } from "Utils/Global/globalConstants";
import { ICreateCategoryProps } from "@model/Category.model";

import { CategoriesService } from "../../../Logic/CategoriesService";
import { UserAuthenticationStoreImplementation } from "Data/UserAuthentication/UserAuthenticationStoreImplementation";
import { useForceLogoutAndRedirect } from "../../../Logic/Functions/Hooks/useForceLogoutAndRedirect";
import { useNavigate } from "react-router-dom";
import { UnauthorizedPopupStoreImplementation } from "Data/UnauthorizedPopup/UnauthorizedPopupStoreImplementation";

const TITLE_MAX = 50;
const DESC_MAX = 500;

const CreateCategoryForm: React.FC = () => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<ICreateCategoryProps>({
    mode: "onSubmit",
    defaultValues,
  });

  const [apiErrorMessage, setApiErrorMessage] = useState<string | undefined>(
    undefined
  );
  const onAuthError = useForceLogoutAndRedirect();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false); // NEW: local loading state for create
  const { showPopup } = UnauthorizedPopupStoreImplementation();
  const navigate = useNavigate();
  const titleValue = useWatch({ control, name: "title" }) ?? "";
  const descriptionValue = useWatch({ control, name: "description" }) ?? "";

  const categoriesService = new CategoriesService();
  const authStore = UserAuthenticationStoreImplementation();

  const onValid = async (formValues: ICreateCategoryProps) => {
    setApiErrorMessage(undefined);
    setSuccessMessage(null);

    await categoriesService.handleCreateCategory({
      data: formValues,
      token: authStore.authenticationState?.token ?? "",
      setLoading: setIsCreating,
      setApiError: (msg) => setApiErrorMessage(msg ?? undefined),
      setSuccessMsg: (msg) => setSuccessMessage(msg),
      reset: () => reset(defaultValues),
      onAuthError,
      onForbidden: (msg) => {
        showPopup(msg);
        navigate("/", { replace: true });
      }
    });
  };

  const onInvalid = () => {
    setSuccessMessage(null);
    setApiErrorMessage("Please fix the errors and try again.");
  };

  const onCancel = () => {
    reset(defaultValues);
    setApiErrorMessage(undefined);
    setSuccessMessage(null);
  };

  return (
    <div className={styles.catPageWrapper} data-testid="cc-page">
      <div className={styles.catContainerNarrow} data-testid="cc-container">
        <div className={styles.catCard} data-testid="cc-card">
          <div className={styles.catCardHeader} data-testid="cc-header">
            <div>
              <h2 className={styles.catCardTitle} data-testid="cc-title">
                New Category
              </h2>
              <p className={styles.catCardSub} data-testid="cc-subtitle">
                Fill in details below
              </p>
            </div>
          </div>

          <form
            className={styles.catCardBody}
            onSubmit={handleSubmit(onValid, onInvalid)}
            data-testid="cc-form"
          >
            <section
              className={styles.catCardSection}
              data-testid="cc-section-title"
            >
              <div className={styles.catSectionTop}>
                <label className={styles.catLabel} htmlFor="cc-title-input">
                  Title
                </label>
                <span
                  className={styles.catCounter}
                  data-testid="cc-title-counter"
                >
                  {titleValue.length}/{TITLE_MAX}
                </span>
              </div>

              <Controller
                name="title"
                control={control}
                rules={{
                  required: "Title is required",
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= TITLE_MAX ||
                    `Title must be at most ${TITLE_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <TextInput
                      label=""
                      placeholder="Enter category title"
                      value={field.value}
                      onChange={field.onChange}
                      error={fieldState.error?.message}
                      data-testid="cc-title-input"
                    />
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles.catCardSection}
              data-testid="cc-section-description"
            >
              <div className={styles.catSectionTop}>
                <label
                  className={styles.catLabel}
                  htmlFor="cc-description-input"
                >
                  Description
                </label>
                <span
                  className={styles.catCounter}
                  data-testid="cc-desc-counter"
                >
                  {descriptionValue.length}/{DESC_MAX}
                </span>
              </div>

              <Controller
                name="description"
                control={control}
                rules={{
                  required: "Description is required",
                  validate: (v) =>
                    (v?.trim().length ?? 0) <= DESC_MAX ||
                    `Description must be at most ${DESC_MAX} characters`,
                }}
                render={({ field, fieldState }) => (
                  <Fragment>
                    <textarea
                      id="cc-description-input"
                      className={styles.contactTextarea}
                      placeholder="Write the category description..."
                      rows={6}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      data-testid="cc-description-input"
                    />

                    {fieldState.error?.message && (
                      <div
                        className={`${styles.formErrorText} ${styles.carMargintop}`}
                        data-testid="cc-description-error"
                      >
                        {fieldState.error.message}
                      </div>
                    )}
                  </Fragment>
                )}
              />
            </section>

            <section
              className={styles.catCardSection}
              data-testid="cc-section-image"
            >
              <div className={styles.catSectionTop}>
                <label className={styles.catLabel}>Image</label>
              </div>

              <Controller
                name="image"
                control={control}
                rules={{
                  validate: (fl: FileList) => {
                    const f = fl?.[0];
                    if (!f) return "Image is required";
                    const valid = [
                      "image/png",
                      "image/jpeg",
                      "image/jpg",
                      "image/webp",
                    ];
                    if (!valid.includes(f.type))
                      return "Image must be PNG, JPG, or WEBP";
                    if (f.size > 5 * 1024 * 1024) return "Image must be ≤ 5MB";
                    return true;
                  },
                }}
                render={({ field, fieldState }) => (
                  <FileUpload
                    value={field.value}
                    onChange={field.onChange}
                    mimeTypes={[
                      "image/png",
                      "image/jpeg",
                      "image/jpg",
                      "image/webp",
                    ]}
                    required
                    showInlineError={false}
                    helperText="PNG, JPG, or WEBP ≤ 5MB"
                    error={fieldState.error?.message || null}
                    data-testid="cc-image"
                    parentTestId="cc-image"
                  />

                )}
              />
            </section>

            {successMessage && (
              <div className={styles.successBanner} data-testid="cc-success">
                {successMessage}
              </div>
            )}
            {apiErrorMessage && (
              <div className={styles.formErrorText} data-testid="cc-api-error">
                {apiErrorMessage}
              </div>
            )}

            <div className={styles.catCardFooter} data-testid="cc-footer">
              <div className={styles.catActionsRow} data-testid="cc-actions">
                <button
                  type="button"
                  className={styles.catBtnGhost}
                  onClick={onCancel}
                  data-testid="cc-cancel"
                >
                  Cancel
                </button>

                <Submission
                  error={undefined}
                  disabled={isSubmitting || isCreating}
                  buttonText={
                    isSubmitting || isCreating
                      ? "Creating..."
                      : "Create Category"
                  }
                  className={styles.catBtn}
                  data-testid="cc-submit"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCategoryForm;
